import { TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { AwsNetworkOutput, AwsStorageOutput, DatabaseStorage, VirtualServerStorageOutput } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Box, ColumnLayout, Container, Header, SpaceBetween, Spinner, Tabs } from '@amzn/awsui-components-react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { match, useHistory, useRouteMatch } from 'react-router-dom';
import { useIsMounted } from 'react-tidy';

import { HelpPanelTopicOptions } from '../../../../common/Constants';
import { constants, DatabaseWarningMessages, StorageWarningMessages } from '../../../../common/Localization';
import { formatCurrencyNoDecimals } from '../../../../common/LocalizationHelper';
import { MatchParam } from '../../../../common/types';
import { HelpPanelContext } from '../../../../contexts/HelpPanelContext';
import { TcoContext } from '../../../../contexts/TcoContext';
import { buildStorageAndNetworkSummary } from '../../common/Common';
import { ErrorMsgFormat } from '../../error-handler-context/CommonTcoErrorHandler';
import GenerateErrorMessageBox from '../../error-handler-context/TcoErrorHandler';
import {
    buildAwsEbsStorageSummaryTable,
    buildAwsEfsStorageSummaryTable,
    buildAwsNetworkSummaryTable,
    buildAwsObjectStorageSummaryTable,
    buildAwsRdsStorageSummaryTable,
    buildAwsStorageNetworkTabs,
} from './AWSStorageNetworkCostHelper';

type Props = FlashContextInterface;

const tcoService = new TcoService();

type UnmatchedDatabase = {
    [errorId in keyof typeof DatabaseWarningMessages]?: string[];
};

function AWSStorageAndNetworkCost({ flash }: Props) {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });
    const tcoAssessmentId = match?.params.tcoAssessmentId ?? null;
    const portfolioId = match?.params.portfolioId ?? null;
    const { assessment, tcoLoading } = useContext(TcoContext);
    const [ loading, setLoading ] = useState(false);
    const { panelHandler } = useContext(HelpPanelContext);
    const isMounted = useIsMounted();

    const history = useHistory();
    const { alert } = flash;
    const [ awsNetworkOutput, setAwsNetworkOutput ] = useState<AwsNetworkOutput | null>(null);
    const [ awsStorageOutput, setAwsStorageOutput ] = useState<AwsStorageOutput | null>(null);
    const [ activeTabId, setActiveTabId ] = useState('ebsGeneralPurpose');
    const [ databasesWithoutErrors, setDatabaseWithoutErrors ] = useState<DatabaseStorage[]>([]);
    const [ errorMessages, setErrorMessages ] = useState<ErrorMsgFormat[]>([]);

    const loadStorageNetworkData = useCallback(async () => {
        if (!isMounted()) {
            return;
        }

        setLoading(true);
        try {
            if (tcoAssessmentId && tcoAssessmentId !== 'default' && assessment) {
                const networkAssessment = await tcoService.getAwsNetwork(tcoAssessmentId);
                const storageAssessment = await tcoService.getAwsStorage(tcoAssessmentId);
                if (isMounted()) {
                    setAwsNetworkOutput(networkAssessment.output.aws.network);
                    setAwsStorageOutput(storageAssessment.output.aws);
                }
            }
        } catch(err) {
            if (isMounted()) {
                alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_STORAGE);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    },[assessment, alert, isMounted, tcoAssessmentId]);

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.AWS_STORAGE_AND_NETWORK);
    }, [ panelHandler ]);

    useEffect(() => {
        if (assessment) {
            loadStorageNetworkData();
        }
    }, [assessment, loadStorageNetworkData]);

    useEffect(() => {
        if (!awsStorageOutput) {
            return;
        }
        // Find valid databases and invalid databases with specific error messages
        const dbWithoutErrors: DatabaseStorage[] = [];
        const unmatchedDB: UnmatchedDatabase = {
            IOPS_PRICE_MATCH_NOT_FOUND: [],
            STORAGE_PRICE_MATCH_NOT_FOUND: [],
            OVER_STORAGE_MAX: [],
            MAPPED_TO_MISSING_SERVER: [],
        };

        // Get databases and database errors
        awsStorageOutput.storage.rds?.databases.forEach(databaseStorage => {
            if (databaseStorage.error) {
                Object.keys(unmatchedDB).forEach(errorId => {
                    if (errorId == databaseStorage.error) {
                        unmatchedDB[errorId as keyof UnmatchedDatabase]?.push(databaseStorage.databaseId);
                    }
                });
            } else {
                dbWithoutErrors.push(databaseStorage);
            }
        });
        setDatabaseWithoutErrors(dbWithoutErrors);

        const errorMsgs: ErrorMsgFormat[] = [];

        // Generate error messages from unmatched DBs
        for (const [key, value] of Object.entries(unmatchedDB)) {
            if (!value || value.length === 0) {
                continue;
            }
            errorMsgs.push({
                count: value.length,
                targetValue: value,
                targetColumn: 'databaseId',
                path: `/portfolio/${portfolioId}/management/assets/databases`,
                singular: constants.DATABASE_LOWERCASE,
                text: key,
            });
        }

        // Generate error messages from outOfScopeServers
        if ((awsStorageOutput.ec2.excludedServers.outOfScope ?? 0) > 0) {
            errorMsgs.push({
                count: awsStorageOutput.ec2.excludedServers.outOfScope,
                targetColumn: 'inScope',
                targetValue: ['false'],
                path: `/portfolio/${portfolioId}/management/assets/servers`,
                singular: constants.SERVER_LOWERCASE,
                text: StorageWarningMessages.OUT_OF_SCOPE,
            });
        }

        // Generate error messages from excluded shared server
        const storageExcludedSharedItems = Object.values(awsStorageOutput.storage.storageExcludedShared || {}).flat();
        if (storageExcludedSharedItems.length > 0) {
            errorMsgs.push({
                count: storageExcludedSharedItems.length,
                path: `/portfolio/${portfolioId}/management/assets/additional-storage`,
                singular: constants.SHARED_STORAGE_ITEM,
                text: StorageWarningMessages.SHARED_STORAGE_EXCLUSION,
            });
        }

        // Generate error messages from storage excluded servers
        Object.keys(awsStorageOutput.storageExcludedServers).forEach(key => {
            const ids = awsStorageOutput.storageExcludedServers[key];
            errorMsgs.push({
                count: ids.length,
                targetValue: ids,
                targetColumn: 'serverId',
                path: `/portfolio/${portfolioId}/management/assets/servers`,
                singular: constants.SERVER_LOWERCASE,
                text: StorageWarningMessages[key as keyof typeof StorageWarningMessages] ?? constants.WITH_UNEXPECTED_ERROR,
            });
        });

        setErrorMessages(errorMsgs);
    }, [ awsStorageOutput, portfolioId ]);

    const buildAwsStorageNetworkContent = (): JSX.Element => {
        if (tcoLoading || loading || !awsStorageOutput || !awsNetworkOutput || !assessment) {
            return (
                <Spinner size='big' />
            );
        }
        const storage = awsStorageOutput.storage;
        const currency = awsNetworkOutput.currency;
        const physicalServers: VirtualServerStorageOutput[] = awsStorageOutput.ec2.servers.filter(server => !!server.ebs).map(ebsServer => {
            return {
                serverId: ebsServer.serverId,
                volumeType: ebsServer.ebs.volume.type,
                volumeApi: ebsServer.ebs.volume.api,
                volumeSizeInGB: ebsServer.ebs.volume.totalSize,
                iops: ebsServer.ebs.piops.totalIOPS,
                throughput: ebsServer.ebs.throughput?.totalThroughputMB || 0,
                volumeCost: ebsServer.ebs.volume.totalCost,
                snapshotSizeInGB: ebsServer.ebs.snapshot.totalSize,
                iopsCost: ebsServer.ebs.piops.totalCost,
                throughputCost: ebsServer.ebs.throughput?.totalCost || 0,
                snapshotCost: ebsServer.ebs.snapshot.totalCost,
                totalCost: ebsServer.ebs.volume.totalCost + ebsServer.ebs.piops.totalCost
                    + ebsServer.ebs.snapshot.totalCost + (ebsServer.ebs.throughput?.totalCost || 0),
                volumeTypeApi: ebsServer.ebs.volume.api ? ebsServer.ebs.volume.type + ' (' + ebsServer.ebs.volume.api + ')' : ebsServer.ebs.volume.type,
            };
        });

        const virtualServers: VirtualServerStorageOutput[] = storage.virtualServerStorage.map(virtualServer => {
            return {
                ...virtualServer,
                volumeTypeApi: virtualServer.volumeApi ? virtualServer.volumeType + ' (' + virtualServer.volumeApi + ')' : virtualServer.volumeType,
            };
        });

        const allServers = physicalServers.concat(virtualServers);

        return (
            <SpaceBetween size='l'>
                <GenerateErrorMessageBox
                    errorMessages={errorMessages}
                    history={history}
                />
                <Header variant='h1'>{constants.AWS_STORAGE_COST_TITLE}</Header>
                {buildAwsEbsStorageSummaryTable(storage.ebs, currency)}
                {buildAwsRdsStorageSummaryTable(storage.rds.storageTypes, currency)}
                {buildAwsObjectStorageSummaryTable(storage.s3, currency)}
                {buildAwsEfsStorageSummaryTable(storage.efs, currency)}
                <ColumnLayout disableGutters columns={2} variant='default'>
                    <Box variant='h3' float='left'><strong>{constants.AWS_STORAGE_TOTAL}</strong></Box>
                    <Box variant='h3' float='right'><strong>{formatCurrencyNoDecimals(storage.summary.totalCost, currency)}</strong></Box>
                </ColumnLayout>
                <Header variant='h1'>{constants.AWS_NETWORK_COST_TITLE}</Header>
                {buildAwsNetworkSummaryTable(awsNetworkOutput.networkList, currency)}
                <ColumnLayout disableGutters columns={2} variant='default'>
                    <Box variant='h3' float='left'><strong>{constants.AWS_NETWORK_TOTAL}</strong></Box>
                    <Box variant='h3' float='right'><strong>
                        {formatCurrencyNoDecimals(awsNetworkOutput.summary.totalCost, currency)}
                    </strong></Box>
                </ColumnLayout>
                <Header variant='h1'>{constants.TOTAL}</Header>
                {buildStorageAndNetworkSummary(
                    storage.summary.totalCost + awsNetworkOutput.summary.totalCost,
                    storage.summary.manualAdjustment,
                    awsNetworkOutput.summary.manualAdjustment,
                    storage.summary.adjustedTotalCost + awsNetworkOutput.summary.adjustedTotalCost,
                    currency,
                    storage.summary.manualAdjustmentDescription,
                    awsNetworkOutput.summary.manualAdjustmentDescription,
                )}
                <Header variant='h2'>{constants.DETAILS}</Header>
                <Tabs
                    tabs={buildAwsStorageNetworkTabs(storage.summary.annualCostsByStorageType, allServers, databasesWithoutErrors ,awsNetworkOutput, currency,
                        assessment.configuration.awsStorage.snapshotCompressionRatio)}
                    activeTabId={activeTabId}
                    onChange={({ detail }) => setActiveTabId(detail.activeTabId)}></Tabs>
            </SpaceBetween>
        );
    };

    return (
        <Container data-testid='container'>
            {buildAwsStorageNetworkContent()}
        </Container>
    );
}

export default withFlashContext(AWSStorageAndNetworkCost);
